import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, ArrowUpRight, Clock, GraduationCap, MapPin, Video, CheckCircle2, Users } from "lucide-react";
import { fr } from "date-fns/locale";
import { JOURS_LONGS, MOIS } from "@/lib/dates";
import { api } from "@/api";
import FormationBookingDialog from "@/components/FormationBookingDialog";

export default function Formations() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [bookingSession, setBookingSession] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/public/formations/sessions");
      setSessions(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const sessionDates = useMemo(
    () => sessions.map((s) => new Date(s.date).toISOString().slice(0, 10)),
    [sessions]
  );
  const displayed = selectedDate
    ? sessions.filter((s) => new Date(s.date).toDateString() === selectedDate.toDateString())
    : sessions;

  const buildMailto = (s) => {
    const subject = `Réservation formation PAG — ${s.title}`;
    const body = [
      "Bonjour,",
      "",
      `Je souhaite réserver une place pour la session de formation suivante :`,
      `• Formation : ${s.title}`,
      `• Date : ${JOURS_LONGS[s.date.getDay()]} ${s.date.getDate()} ${MOIS[s.date.getMonth()]} ${s.date.getFullYear()} à ${String(s.date.getHours()).padStart(2, "0")}h${String(s.date.getMinutes()).padStart(2, "0")}`,
      `• Durée : ${s.duree}`,
      `• Mode : ${s.mode}`,
      "",
      "Mes coordonnées :",
      "• Nom / Prénom : _____________________",
      "• Entreprise : _____________________",
      "• Code PAG-ENT : _____________________",
      "• Téléphone : _____________________",
      "",
      "Cordialement,",
    ].join("\n");
    return `mailto:contact@parcavenue.eu?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="formations-back">
            <img src="/pag-logo.png" alt="PAG" className="h-10 w-10 object-contain" />
            <div className="flex flex-col">
              <span className="font-display font-black text-xl tracking-tight leading-none">PAG</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold mt-0.5">Business Manager</span>
            </div>
          </Link>
          <Link to="/" className="text-sm font-semibold flex items-center gap-2 hover:text-[#002FA7] transition-colors" data-testid="formations-home">
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-4">// Formations Parc Avenue</div>
            <h1 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]" data-testid="formations-title">
              Maîtrisez PAG<br />
              en quelques heures.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
              Le groupe <span className="text-white font-semibold">Parc Avenue</span> organise chaque semaine des sessions de formation
              pour les chefs d'entreprise et leurs équipes. <b>Incluses</b> dans l'abonnement PAG Business Manager.
            </p>
          </div>
          <div className="lg:col-span-4 grid grid-cols-3 gap-0 border border-white">
            <HeroStat value="2/sem" label="Sessions" />
            <HeroStat value="8" label="Places max" />
            <HeroStat value="0€" label="Incluses" />
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-4">// Modules disponibles</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-0 border border-[#0A0A0A]">
            {["Prise en main", "Plannings & Congés", "Paie & Bulletins", "Documents RH", "Cas pratiques"].map((t, i) => (
              <div key={t} className={`p-5 ${i < 4 ? "lg:border-r border-[#0A0A0A]" : ""} ${i < 4 ? "border-b lg:border-b-0 border-[#0A0A0A]" : ""}`}>
                <div className="text-xs font-mono text-[#002FA7] font-bold mb-2">0{i + 1}</div>
                <GraduationCap className="h-5 w-5 text-[#002FA7] mb-3" />
                <div className="font-display font-bold tracking-tight">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar + sessions */}
      <section className="bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-12 gap-8">
          {/* Left: calendar */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-4">// Calendrier</div>
            <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight mb-6">Choisissez votre session</h2>
            <div className="bg-white border border-[#DEE2E6] p-4" data-testid="formations-calendar">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={fr}
                weekStartsOn={1}
                modifiers={{ session: (d) => sessionDates.includes(d.toISOString().slice(0, 10)) }}
                modifiersStyles={{ session: { backgroundColor: "#002FA7", color: "white", fontWeight: "bold" } }}
                className="w-full"
              />
              <div className="mt-4 pt-4 border-t border-[#DEE2E6] flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 bg-[#002FA7]" />
                  <span className="text-[#495057] font-semibold uppercase tracking-wider">Session disponible</span>
                </div>
                {selectedDate && (
                  <button onClick={() => setSelectedDate(null)} className="text-[#002FA7] font-semibold hover:underline" data-testid="calendar-reset">
                    Voir toutes
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right: sessions list */}
          <div className="lg:col-span-7">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-2">// Sessions à venir</div>
                <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight">
                  {selectedDate
                    ? `${JOURS_LONGS[selectedDate.getDay()]} ${selectedDate.getDate()} ${MOIS[selectedDate.getMonth()]}`
                    : `${displayed.length} prochaines sessions`}
                </h2>
              </div>
            </div>

            <div className="space-y-3" data-testid="formations-sessions">
              {displayed.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#868E96] bg-white border border-[#DEE2E6]">
                  Aucune session ce jour-là. Sélectionnez une autre date.
                </div>
              ) : displayed.map((s) => (
                <SessionCard key={s.id} session={s} onBook={() => setBookingSession(s)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-black tracking-tight mb-2">Une question sur les formations ?</h3>
            <p className="text-white/60 text-sm">Notre équipe vous répond du lundi au vendredi, 9h-18h.</p>
          </div>
          <div className="flex gap-3">
            <a href="mailto:contact@parcavenue.eu" className="inline-flex items-center h-12 px-6 bg-[#FFD700] text-[#0A0A0A] hover:bg-white transition-colors font-semibold text-sm" data-testid="formations-cta-email">
              contact@parcavenue.eu <ArrowUpRight className="ml-2 h-4 w-4" />
            </a>
            <a href="tel:+33970701792" className="inline-flex items-center h-12 px-6 border border-white/30 hover:bg-white hover:text-[#0A0A0A] transition-colors font-semibold text-sm font-mono" data-testid="formations-cta-tel">
              09 70 70 17 92
            </a>
          </div>
        </div>
      </section>

      <FormationBookingDialog
        open={!!bookingSession}
        onOpenChange={(o) => { if (!o) setBookingSession(null); }}
        session={bookingSession}
        onBooked={load}
      />
    </div>
  );
}

function HeroStat({ value, label }) {
  return (
    <div className="p-4 text-center border-r last:border-r-0 border-white/20">
      <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">{label}</div>
    </div>
  );
}

function SessionCard({ session, onBook }) {
  const d = new Date(session.date);
  const dateStr = `${JOURS_LONGS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]}`;
  const timeStr = `${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
  const isOnline = session.mode.toLowerCase().includes("visio");
  const full = session.places_restantes === 0;
  return (
    <div className="bg-white border border-[#DEE2E6] p-5 hover:border-[#0A0A0A] transition-colors" data-testid="session-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-semibold text-[#002FA7] mb-2">
            <span>{dateStr}</span>
            <span className="text-[#DEE2E6]">·</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {timeStr}</span>
            <span className="text-[#DEE2E6]">·</span>
            <span>{session.duree}</span>
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-2">{session.title}</h3>
          {session.description && <p className="text-sm text-[#495057] leading-relaxed mb-3">{session.description}</p>}
          <div className="flex items-center gap-4 text-xs text-[#495057] flex-wrap">
            <span className="flex items-center gap-1">
              {isOnline ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />} {session.mode}
            </span>
            <span className={`flex items-center gap-1 font-semibold ${full ? "text-[#E60000]" : ""}`}>
              <Users className="h-3 w-3" /> {full ? "Complet" : `${session.places_restantes} place${session.places_restantes > 1 ? "s" : ""} restante${session.places_restantes > 1 ? "s" : ""}`}
            </span>
            <span className="flex items-center gap-1 text-[#059669] font-semibold">
              <CheckCircle2 className="h-3 w-3" /> Incluse
            </span>
          </div>
        </div>
        <button
          onClick={onBook}
          disabled={full}
          data-testid="session-book"
          className="inline-flex items-center h-10 px-4 bg-[#002FA7] hover:bg-[#002175] disabled:bg-[#DEE2E6] disabled:text-[#868E96] disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors whitespace-nowrap"
        >
          {full ? "Complet" : "Réserver"} {!full && <ArrowUpRight className="ml-2 h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
